<template>
	<div class="upgrades-container">
		<div
			v-if="upgrades && filteredUpgrades(upgrades).length"
			class="upgrades"
		>
			<UpgradeBox
				v-for="(upgrade, index) in filteredUpgrades(upgrades)"
				:key="index"
				:id="upgrade[0]"
				:heading="upgrade[1].heading"
				:description="upgrade[1].description"
				:descriptionReplace="upgrade[1].descriptionReplace"
				:price="getUpgradePrice(upgrade)"
				:currentLevel="getCurrentLevelById(upgrade[0])"
				:maxLevel="upgrade[1].maxLevel"
				:isNew="getIsNew(upgrade[0])"
				:isStarred="upgrade[1].isStarred"
				:upgrade-callback="upgrade[1].upgradeCallback"
				:isEventType="isEventType"
				:skin="isEventType ? SkinTypes.EVENT : SkinTypes.DARK"
				:eventType="eventType"
			/>
		</div>
		<div class="no-upgrades" v-else>
			<span class="heading">
				{{ translate("ui.no_upgrades_available") }}
			</span>
		</div>
		<p v-if="!isEventType" class="level-up">
			{{ translate("ui.level_up_to_unlock_upgrades") }}
		</p>
	</div>
</template>

<script lang="ts" setup>
import UpgradeBox from "@/components/Upgrades/UpgradeBox.vue";
import type {
	PriceLevels,
	PriceValue,
	UpgradeId,
	UpgradeItem,
} from "@/game/entities/config/upgrades";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import type { EventCategory } from "@/types/ui";
import { IconTypes, SkinTypes } from "@/types/ui";
import { FilterItems } from "@/types/upgrades";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";

interface Props {
	upgrades: Map<UpgradeId, UpgradeItem>;
	isEventType?: boolean;
	eventType?: EventCategory;
}
const { upgrades, isEventType = false, eventType } = defineProps<Props>();

const upgradesStore = useUpgradesStore();
const { getCurrentLevelById } = upgradesStore;

const runtimeStore = useRuntimeStore();
const { upgradeFilterValue } = storeToRefs(runtimeStore);

const userStore = useUserStore();
const { upgradeFilterVisibility, newUpgradesIds } = storeToRefs(userStore);
const { setNewUpgradesIds } = userStore;

const { mainLevel } = storeToRefs(useSettlementsStore());

const router = useRouter();

export type FilteredUpgrade = [UpgradeId, UpgradeItem];

const filteredUpgrades = (
	upgrades: Map<UpgradeId, UpgradeItem>,
): Array<FilteredUpgrade> => {
	let filteredArray = Array.from(upgrades.entries()) as Array<
		[UpgradeId, UpgradeItem]
	>;
	filteredArray = filteredArray.filter(
		(upgrade: FilteredUpgrade): boolean => upgrade[1].levelUnlock > 0,
	);
	filteredArray = filteredArray.filter(
		(upgrade: [UpgradeId, UpgradeItem]): boolean =>
			upgrade[1].levelUnlock <= mainLevel.value ||
			getCurrentLevelById(upgrade[0]) > 0,
	);
	if (upgradeFilterVisibility.value === IconTypes.HIDE) {
		filteredArray = filteredArray.filter(
			(upgrade: [UpgradeId, UpgradeItem]): boolean =>
				getCurrentLevelById(upgrade[0]) < upgrade[1].maxLevel,
		);
	}
	if (upgradeFilterValue.value === FilterItems.NEWEST) {
		filteredArray = filteredArray.sort(
			(
				upgradeA: [UpgradeId, UpgradeItem],
				upgradeB: [UpgradeId, UpgradeItem],
			) => {
				return upgradeB[1].levelUnlock - upgradeA[1].levelUnlock;
			},
		);
	}
	if (upgradeFilterValue.value === FilterItems.CHEAPEST) {
		let filteredArrayPremium = filteredArray.filter(upgrade =>
			isPremium(upgrade[1].price[1]),
		);
		let filteredArrayNonPremium = filteredArray.filter(
			upgrade => !isPremium(upgrade[1].price[1]),
		);
		filteredArrayPremium = filteredArrayPremium.sort(
			(
				upgradeA: [UpgradeId, UpgradeItem],
				upgradeB: [UpgradeId, UpgradeItem],
			) => {
				const currentPriceA =
					upgradeA[1].price[getCurrentLevelById(upgradeA[0]) + 1];
				const currentPriceB =
					upgradeB[1].price[getCurrentLevelById(upgradeB[0]) + 1];
				return (currentPriceA?.gems || 0) - (currentPriceB?.gems || 0);
			},
		);
		filteredArrayNonPremium = filteredArrayNonPremium.sort(
			(
				upgradeA: [UpgradeId, UpgradeItem],
				upgradeB: [UpgradeId, UpgradeItem],
			) => {
				const currentPriceA =
					upgradeA[1].price[getCurrentLevelById(upgradeA[0]) + 1];
				const currentPriceB =
					upgradeB[1].price[getCurrentLevelById(upgradeB[0]) + 1];
				return (
					(currentPriceA?.coins || 0) - (currentPriceB?.coins || 0)
				);
			},
		);
		filteredArray = [...filteredArrayNonPremium, ...filteredArrayPremium];
		filteredArray.sort(
			(
				upgradeA: [UpgradeId, UpgradeItem],
				upgradeB: [UpgradeId, UpgradeItem],
			) => {
				const currentPriceA =
					upgradeA[1].price[getCurrentLevelById(upgradeA[0]) + 1];
				const currentPriceB =
					upgradeB[1].price[getCurrentLevelById(upgradeB[0]) + 1];
				if (currentPriceA === undefined) return 1;
				if (currentPriceB === undefined) return -1;
				return 1;
			},
		);
	}
	if (upgradeFilterValue.value === FilterItems.PREMIUM) {
		filteredArray = filteredArray.filter(upgrade =>
			isPremium(upgrade[1].price[1]),
		);
	}
	return filteredArray;
	// TODO: Dorobit filtrovanie specialnych upgradeov
};

const getUpgradePrice = (upgrade: [UpgradeId, UpgradeItem]): PriceValue => {
	if (!upgrade || !upgrade?.[1]?.price) return {};

	const currentLevel = getCurrentLevelById(upgrade[0]);

	const upgradePrices = upgrade[1]?.price as PriceLevels;
	const currentPrice = upgradePrices[currentLevel + 1] || [];
	return currentPrice;
};

const isPremium = (price: Record<string, number>): boolean => {
	if (!price || Object.keys(price).length > 1) return false;
	return "gems" in price;
};

const getIsNew = (upgradeId: UpgradeId): boolean => {
	return newUpgradesIds.value.includes(upgradeId);
};

onMounted((): void => {
	if (isEventType) {
		router.replace({
			query: {
				...router.currentRoute.value.query,
				event: `${isEventType}`,
			},
		});
	} else {
		router.replace({
			query: {
				...router.currentRoute.value.query,
				event: undefined,
			},
		});
	}
});

onUnmounted((): void => {
	setNewUpgradesIds([]);
});
</script>

<style lang="scss" scoped>
.upgrades-view {
	.upgrades-container {
		@include flex-col-center;
		flex: 1 1 0;
	}

	.upgrades {
		@include flex-col-center;
		gap: 0.5rem;
		width: 100%;
	}

	.no-upgrades {
		flex: 1 1 auto;
		@include flex-col-center;

		.heading {
			color: #f2dcb5;
			font-weight: 800;
			font-size: 1rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
	}

	.level-up {
		margin: 0.563rem;
		color: #fff3dd;
		font-size: 0.75rem;
		line-height: 0.75rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}
}

.about-upgrades {
	@include flex-col-center;
	flex: 1 1 0;
	color: #372615;
	font-size: 0.875rem;
	line-height: 1rem;
	text-align: center;
}
</style>
